import { computed, onMounted, ref } from 'vue'
import { useServerList, type AppTablePaginationModel, type AppTableSortModel } from '@staff/ui'

import {
  fetchDistributorApplications,
  type DistributorApplicationLifecycleStatus,
  type DistributorApplicationListItem,
  type DistributorApplicationListQuery,
  type DistributorApplicationListResponse,
  type DistributorApplicationType,
} from '@/services/application'
import type { WorkResponsibilityParty } from '@staff/shared'

export type DistributorApplicationListFilters = {
  status?: DistributorApplicationLifecycleStatus
  type?: DistributorApplicationType
  hasUnreadMessages?: boolean
  hasOpenConversations?: boolean
  hasOpenRequiredAdditions?: boolean
  isHighPrio?: boolean
  workResponsibilityParty?: WorkResponsibilityParty
  brokerPersonnelUserId?: string
}

export type DistributorApplicationListSortField = 'submitted_at' | 'work_responsibility_since'

export type DistributorApplicationsTableModel = {
  items: DistributorApplicationListItem[]
  loading: boolean
  pagination: AppTablePaginationModel
  sort: AppTableSortModel<DistributorApplicationListSortField>
}

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100]

export const useDistributorApplicationSearch = () => {
  const filters = ref<DistributorApplicationListFilters>({})

  const list = useServerList<
    DistributorApplicationListItem,
    DistributorApplicationListFilters,
    DistributorApplicationListQuery,
    DistributorApplicationListResponse,
    DistributorApplicationListSortField
  >({
    filters,
    initialPageSize: 25,
    initialSort: { field: 'submitted_at', direction: 'desc' },
    fetch: (query) => fetchDistributorApplications(query),
    toRequest: ({ filters: currentFilters, pageIndex, pageSize, sort }) => ({
      ...currentFilters,
      page: pageIndex,
      hitsPerPage: pageSize,
      orderBy: sort?.field ?? 'submitted_at',
      orderDirection: sort?.direction ?? 'desc',
    }),
    fromResponse: (response) => ({
      items: response.hits,
      totalItems: response.nbHits,
    }),
  })

  const handleUpdateFilters = async (data: DistributorApplicationListFilters) => {
    filters.value = data
    list.resetPageIndex()
    await list.search()
  }

  // Runs the initial search, so callers don't wire up their own onMounted. Relies on being called
  // from setup() — calling this composable outside a component will never load any data.
  onMounted(() => {
    void list.search()
  })

  const tableModel = computed<DistributorApplicationsTableModel>(() => ({
    items: list.items.value,
    loading: list.isLoading.value,
    pagination: {
      pageStart: list.pageStart.value,
      pageStop: list.pageStop.value,
      totalItems: list.totalItems.value,
      currentPage: list.currentPage.value,
      totalPages: list.totalPages.value,
      pageSize: list.pageSize.value,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      pageSizeText: 'Visa:',
      isPreviousPageDisabled: list.isPreviousPageDisabled.value,
      isNextPageDisabled: list.isNextPageDisabled.value,
      setPageSize: list.setPageSize,
      previousPage: list.previousPage,
      nextPage: list.nextPage,
    },
    sort: {
      field: list.sort.value?.field ?? null,
      direction: list.sort.value?.direction ?? null,
      toggle: list.toggleSort,
    },
  }))

  return {
    filters,
    tableModel,
    isLoading: list.isLoading,
    error: list.error,
    handleUpdateFilters,
  }
}
